import { Component, computed, inject, input, output, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { InformeCumplimiento } from 'src/app/domain/model/informe/informe-cumplimiento';
import { ContactoInforme } from 'src/app/domain/model/informe/contacto-informe';
import { InformeCumplimientoRepository } from 'src/app/domain/service/informe/informe-cumplimiento-repository';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import { sanitizeText, sanitizeEmail, emailKeypress, containsDangerousContent } from 'src/app/common/input-utils';

/** Legacy: DetalleInformeViewImpl.java + DetalleInformeActivity.java */
@Component({
  selector: 'app-detalle-informe-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    TextareaModule,
    TableModule,
    TooltipModule,
    MessageModule,
    ConfirmDialogModule
  ],
  template: `
    <p-confirmDialog key="eliminarContacto" />
    <p-dialog
      header="Detalle Informe"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '95vw', maxWidth: '52rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '92vw', '768px': '95vw', '640px': '98vw' }"
      [closable]="true"
      (onHide)="cerrar()"
    >
      <div class="flex flex-col gap-4">
        <!-- Observaciones readonly -->
        <div>
          <label class="block text-sm font-medium mb-1">Observaciones</label>
          <textarea pTextarea [value]="informe().dsobservaciones ?? ''"
            readonly rows="4" class="w-full"></textarea>
        </div>

        <!-- Listado de Contactos -->
        <div class="border rounded dark:border-gray-700">
          <div class="font-medium text-gray-700 dark:text-gray-300 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center">
            <span>Listado de Contactos</span>
            @if (contactos().length > 0) {
              <span class="text-xs text-gray-500 dark:text-gray-400">
                Registros del {{ registroDesde() }} hasta el {{ registroHasta() }} de {{ totalRegistros() }}
              </span>
            }
          </div>

          @if (!cargando() && contactos().length === 0) {
            <div class="text-center py-4 text-gray-500 dark:text-gray-400">No se encontraron contactos.</div>
          }

          <!-- Mobile -->
          <div class="md:hidden space-y-3 p-3">
            @for (contacto of paginatedData(); track contacto.nmconsecutivo; let i = $index) {
              <div class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900">
                @if (editandoContacto.nmconsecutivo === contacto.nmconsecutivo && editandoIndex() !== null) {
                  <div class="grid grid-cols-1 gap-2">
                    <input pInputText [(ngModel)]="editandoContacto.dsnombreContacto"
                      (input)="editandoContacto.dsnombreContacto = sanitizeText(editandoContacto.dsnombreContacto)"
                      class="w-full" placeholder="Nombre" maxlength="200" />
                    <input pInputText [(ngModel)]="editandoContacto.dsmailContacto"
                      (keypress)="emailKeypress($event)"
                      (input)="editandoContacto.dsmailContacto = sanitizeEmail(editandoContacto.dsmailContacto)"
                      class="w-full" type="email" placeholder="Email" maxlength="200" />
                    <div class="flex justify-end gap-2">
                      <button pButton icon="pi pi-check" class="p-button-text p-button-success p-button-sm min-w-[44px] min-h-[44px]"
                        pTooltip="Guardar" (click)="guardarEdicion()"></button>
                      <button pButton icon="pi pi-times" class="p-button-text p-button-secondary p-button-sm min-w-[44px] min-h-[44px]"
                        pTooltip="Cancelar" (click)="cancelarEdicion()"></button>
                    </div>
                  </div>
                } @else {
                  <div class="grid grid-cols-1 gap-1 text-sm">
                    <div><span class="text-gray-500">Nombre:</span>
                      <span class="text-gray-800 dark:text-gray-200 ml-1">{{ contacto.dsnombreContacto || '-' }}</span></div>
                    <div><span class="text-gray-500">Email:</span>
                      <span class="text-gray-800 dark:text-gray-200 ml-1 break-all">{{ contacto.dsmailContacto || '-' }}</span></div>
                  </div>
                  <div class="flex justify-end gap-2 pt-2">
                    <button pButton icon="pi pi-pencil" class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                      pTooltip="Editar" (click)="editarContacto(contacto)"></button>
                    <button pButton icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                      pTooltip="Eliminar" (click)="confirmarEliminar(contacto)"></button>
                  </div>
                }
              </div>
            }
          </div>

          <!-- Desktop: p-table -->
          <div class="hidden md:block">
            <p-table
              [value]="paginatedData()"
              [loading]="cargando()"
              styleClass="tabla-detalle-contactos p-datatable-sm p-datatable-gridlines"
              [resizableColumns]="true"
              columnResizeMode="expand"
              [scrollable]="true"
              [responsiveLayout]="'scroll'"
              [paginator]="false"
            >
              <ng-template pTemplate="header">
                <tr>
                  <th pResizableColumn><span class="grid-header-text" title="Nombre">Nombre</span></th>
                  <th pResizableColumn><span class="grid-header-text" title="Email">Email</span></th>
                  <th pResizableColumn style="width: 8rem"><span class="grid-header-text" title="Acciones">Acciones</span></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-contacto let-i="rowIndex">
                @if (editandoContacto.nmconsecutivo === contacto.nmconsecutivo && editandoIndex() !== null) {
                  <tr>
                    <td>
                      <input pInputText [(ngModel)]="editandoContacto.dsnombreContacto"
                        (input)="editandoContacto.dsnombreContacto = sanitizeText(editandoContacto.dsnombreContacto)"
                        class="w-full" maxlength="200" />
                    </td>
                    <td>
                      <input pInputText [(ngModel)]="editandoContacto.dsmailContacto"
                        (keypress)="emailKeypress($event)"
                        (input)="editandoContacto.dsmailContacto = sanitizeEmail(editandoContacto.dsmailContacto)"
                        class="w-full" type="email" maxlength="200" />
                    </td>
                    <td>
                      <div class="flex gap-1">
                        <button pButton icon="pi pi-check" class="p-button-text p-button-success p-button-sm min-w-[44px] min-h-[44px]"
                          pTooltip="Guardar" (click)="guardarEdicion()"></button>
                        <button pButton icon="pi pi-times" class="p-button-text p-button-secondary p-button-sm min-w-[44px] min-h-[44px]"
                          pTooltip="Cancelar" (click)="cancelarEdicion()"></button>
                      </div>
                    </td>
                  </tr>
                } @else {
                  <tr>
                    <td><span class="grid-cell-text" [attr.title]="contacto.dsnombreContacto">{{ contacto.dsnombreContacto || '-' }}</span></td>
                    <td><span class="grid-cell-text" [attr.title]="contacto.dsmailContacto">{{ contacto.dsmailContacto || '-' }}</span></td>
                    <td>
                      <div class="flex gap-1">
                        <button pButton icon="pi pi-pencil" class="p-button-text p-button-sm min-w-[44px] min-h-[44px]"
                          pTooltip="Editar" (click)="editarContacto(contacto)"></button>
                        <button pButton icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm min-w-[44px] min-h-[44px]"
                          pTooltip="Eliminar" (click)="confirmarEliminar(contacto)"></button>
                      </div>
                    </td>
                  </tr>
                }
              </ng-template>
              <ng-template pTemplate="emptymessage">
                <tr>
                  <td colspan="3" class="text-center text-gray-500 p-4">No se encontraron contactos.</td>
                </tr>
              </ng-template>
            </p-table>
          </div>

          <!-- Paginación manual -->
          @if (totalRegistros() > 0) {
            <div class="flex justify-between items-center p-3 border-t dark:border-gray-700 mt-2">
              <div class="text-gray-600 dark:text-gray-400 text-sm">
                Página {{ currentPage() }} de {{ totalPages() }}
              </div>
              <div class="flex gap-2 items-center flex-wrap">
                <label class="text-sm text-gray-600 dark:text-gray-400">Registros:</label>
                <p-dropdown
                  [options]="pageSizeOptions"
                  [(ngModel)]="selectedPageSize"
                  (onChange)="onPageSizeChange()"
                  [style]="{ width: '90px' }"
                  appendTo="body"
                  scrollHeight="150px" />
                <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-double-left"
                  severity="secondary" size="small"
                  [disabled]="currentPage() <= 1"
                  (onClick)="irPrimeraPagina()" />
                <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-left"
                  severity="secondary" size="small"
                  [disabled]="currentPage() <= 1"
                  (onClick)="irPaginaAnterior()" />
                <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-right"
                  severity="secondary" size="small"
                  [disabled]="currentPage() >= totalPages()"
                  (onClick)="irPaginaSiguiente()" />
                <p-button styleClass="min-w-[44px] min-h-[44px]" icon="pi pi-angle-double-right"
                  severity="secondary" size="small"
                  [disabled]="currentPage() >= totalPages()"
                  (onClick)="irUltimaPagina()" />
              </div>
            </div>
          }
        </div>

        <!-- Contacto adicional -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm font-medium mb-1">Nombre del contacto</label>
            <input pInputText [(ngModel)]="nuevoNombre" placeholder="Nombre del contacto"
              (ngModelChange)="nuevoNombre = sanitizeText($event)"
              maxlength="200" class="w-full" />
          </div>
          <div>
            <label class="block text-sm font-medium mb-1">Email del contacto</label>
            <input pInputText [(ngModel)]="nuevoEmail" placeholder="Email del contacto"
              (keypress)="emailKeypress($event)"
              (ngModelChange)="nuevoEmail = sanitizeEmail($event)"
              type="email" maxlength="200" class="w-full" />
          </div>
        </div>

        @if (errorMsg()) {
          <p-message severity="error" [text]="errorMsg()" />
        }
      </div>

      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-3 w-full">
          <button pButton label="Cerrar" icon="pi pi-times"
            class="p-button-outlined p-button-secondary"
            (click)="cerrar()"></button>
          <button pButton label="Guardar" icon="pi pi-check"
            [loading]="guardando()"
            [disabled]="cargando()"
            (click)="guardar()"></button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styleUrls: ['../shared/styles/grid-table.styles.scss'],
  providers: [ConfirmationService]
})
export class DetalleInformeDialogComponent implements OnInit {

  informe = input.required<InformeCumplimiento>();
  onClose = output<void>();
  onLoaded = output<void>();
  onLoadingChange = output<boolean>();

  private readonly informeRepo = inject(InformeCumplimientoRepository);
  private readonly toast = inject(MessageService);
  private readonly confirmService = inject(ConfirmationService);

  readonly sanitizeText = sanitizeText;
  readonly sanitizeEmail = sanitizeEmail;
  readonly emailKeypress = emailKeypress;

  visible = true;
  contactos = signal<ContactoInforme[]>([]);
  cargando = signal(true);
  guardando = signal(false);
  errorMsg = signal('');

  // Paginación
  currentPage = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  pageSizeOptions = PAGE_SIZE_OPTIONS;
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  totalRegistros = computed(() => this.contactos().length);
  totalPages = computed(() => Math.ceil(this.totalRegistros() / this.pageSize()) || 1);
  paginatedData = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize();
    return this.contactos().slice(start, start + this.pageSize());
  });
  registroDesde = computed(() => this.totalRegistros() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1);
  registroHasta = computed(() => Math.min(this.currentPage() * this.pageSize(), this.totalRegistros()));

  // Edición inline
  editandoIndex = signal<number | null>(null);
  editandoContacto: ContactoInforme = { dsnombreContacto: '', dsmailContacto: '' };

  // Contacto adicional
  nuevoNombre = '';
  nuevoEmail = '';

  async ngOnInit(): Promise<void> {
    await this.cargarContactos();
  }

  private async cargarContactos(): Promise<void> {
    const id = this.informe()?.nmconsecutivoinforme;
    if (!id) return;
    this.cargando.set(true);
    try {
      // Contrato de release: GET /informes-cumplimiento/{id}/contactos devuelve la lista completa (DTO).
      const contactos = await this.informeRepo.listarContactos(id);
      this.contactos.set(contactos);
    } catch (err) {
      this.errorMsg.set('Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.');
    } finally {
      this.cargando.set(false);
      this.onLoaded.emit();
    }
  }

  onPageSizeChange(): void {
    this.pageSize.set(this.selectedPageSize);
    this.currentPage.set(1);
  }

  irPrimeraPagina(): void { this.currentPage.set(1); }
  irPaginaAnterior(): void { this.currentPage.update(p => Math.max(1, p - 1)); }
  irPaginaSiguiente(): void { this.currentPage.update(p => Math.min(this.totalPages(), p + 1)); }
  irUltimaPagina(): void { this.currentPage.set(this.totalPages()); }

  editarContacto(contacto: ContactoInforme): void {
    this.editandoContacto = { ...contacto };
    this.editandoIndex.set(contacto.nmconsecutivo!);
  }

  cancelarEdicion(): void {
    this.editandoIndex.set(null);
  }

  async guardarEdicion(): Promise<void> {
    const nombre = this.editandoContacto.dsnombreContacto?.trim();
    const email = this.editandoContacto.dsmailContacto?.trim();
    if (!nombre || !email) {
      this.errorMsg.set('El contacto debe tener nombre y email.');
      return;
    }
    if (!this.esEmailValido(email)) {
      this.errorMsg.set('El formato del email es invalido.');
      return;
    }
    this.errorMsg.set('');
    this.onLoadingChange.emit(true);
    const id = this.informe().nmconsecutivoinforme!;
    try {
      await this.informeRepo.actualizarContacto(id, this.editandoContacto);
      this.contactos.update(list =>
        list.map(c => c.nmconsecutivo === this.editandoContacto.nmconsecutivo ? { ...this.editandoContacto } : c)
      );
      this.editandoIndex.set(null);
    } catch (err) {
      this.errorMsg.set('Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.');
    } finally {
      this.onLoadingChange.emit(false);
    }
  }

  confirmarEliminar(contacto: ContactoInforme): void {
    if (this.contactos().length <= 1) {
      this.errorMsg.set('Debe existir al menos un contacto.');
      return;
    }
    this.confirmService.confirm({
      key: 'eliminarContacto',
      message: '¿Está seguro que desea eliminar este contacto?',
      header: 'Confirmar eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      accept: () => this.eliminarContacto(contacto)
    });
  }

  private async eliminarContacto(contacto: ContactoInforme): Promise<void> {
    this.onLoadingChange.emit(true);
    const id = this.informe().nmconsecutivoinforme!;
    try {
      await this.informeRepo.eliminarContacto(id, contacto.nmconsecutivo!);
      this.contactos.update(list => list.filter(c => c.nmconsecutivo !== contacto.nmconsecutivo));
      this.errorMsg.set('');
      this.toast.add({ severity: 'success', summary: 'Éxito', detail: 'Contacto eliminado correctamente' });
    } catch (err) {
      this.errorMsg.set('Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.');
    } finally {
      this.onLoadingChange.emit(false);
    }
  }

  async guardar(): Promise<void> {
    const nombre = this.nuevoNombre?.trim();
    const email = this.nuevoEmail?.trim();

    if ((nombre && !email) || (!nombre && email)) {
      this.errorMsg.set('El contacto adicional debe tener la informacion completa( Nombre - Email )');
      return;
    }

    if (nombre && email) {
      if (containsDangerousContent(nombre)) {
        this.errorMsg.set('El nombre contiene caracteres no permitidos.');
        return;
      }
      if (!this.esEmailValido(email)) {
        this.errorMsg.set('El formato del E-mail para el contacto adicional es invalido');
        return;
      }
    }

    this.errorMsg.set('');
    this.guardando.set(true);
    this.onLoadingChange.emit(true);

    try {
      const id = this.informe().nmconsecutivoinforme!;
      if (nombre && email) {
        await this.informeRepo.crearContactos(id, [{
          dsnombreContacto: sanitizeText(nombre),
          dsmailContacto: email
        }]);
      }
      this.toast.add({ severity: 'success', summary: 'Éxito', detail: 'Contactos guardados correctamente.' });
      this.nuevoNombre = '';
      this.nuevoEmail = '';
      await this.cargarContactos();
    } catch (err) {
      this.errorMsg.set('Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.');
    } finally {
      this.guardando.set(false);
      this.onLoadingChange.emit(false);
    }
  }

  cerrar(): void {
    this.visible = false;
    this.onClose.emit();
  }

  private esEmailValido(email: string): boolean {
    return /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test(email);
  }
}
